/**
 * Linear-time partial JSON parser for streaming SSE responses.
 *
 * Designed to extract fields from incomplete JSON as it arrives token-by-token.
 * Uses character-by-character scanning (O(n)) instead of regex to avoid
 * catastrophic backtracking that caused browser hangs with long strings.
 */

/**
 * Extract a JSON string value starting after the opening quote.
 * Handles standard JSON escape sequences (\", \\, \n, \t, \r, \/).
 * Returns the unescaped string content up to the closing quote,
 * or all content so far if the string is still being streamed.
 */
export const extractJsonStringValue = (json: string, startIndex: number): string | null => {
	let result = "";
	let i = startIndex;
	while (i < json.length) {
		const ch = json[i];
		if (ch === "\\" && i + 1 < json.length) {
			const next = json[i + 1];
			if (next === '"') { result += '"'; i += 2; }
			else if (next === "n") { result += "\n"; i += 2; }
			else if (next === "t") { result += "\t"; i += 2; }
			else if (next === "r") { result += "\r"; i += 2; }
			else if (next === "\\") { result += "\\"; i += 2; }
			else if (next === "/") { result += "/"; i += 2; }
			else { result += ch + next; i += 2; }
		} else if (ch === '"') {
			return result;
		} else {
			result += ch;
			i++;
		}
	}
	return result;
};

/**
 * Find the value start position for a JSON key.
 * Scans past `"key":` with optional whitespace and returns the index
 * of the first character of the value.
 */
export const findValueStart = (json: string, key: string): number => {
	const needle = `"${key}"`;
	const keyIdx = json.indexOf(needle);
	if (keyIdx === -1) return -1;
	let i = keyIdx + needle.length;
	while (i < json.length && (json[i] === " " || json[i] === "\t" || json[i] === "\n" || json[i] === "\r")) i++;
	if (i < json.length && json[i] === ":") i++;
	while (i < json.length && (json[i] === " " || json[i] === "\t" || json[i] === "\n" || json[i] === "\r")) i++;
	return i < json.length ? i : -1;
};

export interface PartialReviewResult {
	score?: number;
	analysis?: string;
	suggestedPrompt?: string | null;
	suggestedScore?: number | null;
	selectedTools?: any[];
}

/**
 * Try to parse a (possibly incomplete) JSON string and extract available fields.
 * First attempts a full JSON.parse; on failure, falls back to linear field extraction.
 */
export const tryParsePartialJson = (jsonString: string): PartialReviewResult | null => {
	try {
		return JSON.parse(jsonString);
	} catch {
		const partialData: PartialReviewResult = {};

		// Extract score (number)
		const scoreStart = findValueStart(jsonString, "score");
		if (scoreStart !== -1 && jsonString[scoreStart] >= "0" && jsonString[scoreStart] <= "9") {
			let numStr = "";
			for (let i = scoreStart; i < jsonString.length && jsonString[i] >= "0" && jsonString[i] <= "9"; i++) {
				numStr += jsonString[i];
			}
			partialData.score = parseInt(numStr, 10);
		}

		// Extract analysis (string)
		const analysisStart = findValueStart(jsonString, "analysis");
		if (analysisStart !== -1 && jsonString[analysisStart] === '"') {
			partialData.analysis = extractJsonStringValue(jsonString, analysisStart + 1);
		}

		// Extract suggestedPrompt (string or null)
		const sugStart = findValueStart(jsonString, "suggestedPrompt");
		if (sugStart !== -1) {
			if (jsonString[sugStart] === '"') {
				partialData.suggestedPrompt = extractJsonStringValue(jsonString, sugStart + 1);
			} else if (jsonString.startsWith("null", sugStart)) {
				partialData.suggestedPrompt = null;
			}
		}

		// Extract suggestedScore (number or null)
		const sugScoreStart = findValueStart(jsonString, "suggestedScore");
		if (sugScoreStart !== -1) {
			if (jsonString[sugScoreStart] >= "0" && jsonString[sugScoreStart] <= "9") {
				let numStr = "";
				for (let i = sugScoreStart; i < jsonString.length && jsonString[i] >= "0" && jsonString[i] <= "9"; i++) {
					numStr += jsonString[i];
				}
				partialData.suggestedScore = parseInt(numStr, 10);
			} else if (jsonString.startsWith("null", sugScoreStart)) {
				partialData.suggestedScore = null;
			}
		}

		// Extract selectedTools (array) — only when complete (closing bracket found)
		const toolsStart = findValueStart(jsonString, "selectedTools");
		if (toolsStart !== -1 && jsonString[toolsStart] === "[") {
			let depth = 0;
			let end = -1;
			for (let i = toolsStart; i < jsonString.length; i++) {
				if (jsonString[i] === "[") depth++;
				else if (jsonString[i] === "]") { depth--; if (depth === 0) { end = i; break; } }
			}
			if (end !== -1) {
				try {
					partialData.selectedTools = JSON.parse(jsonString.slice(toolsStart, end + 1));
				} catch {
					partialData.selectedTools = [];
				}
			}
		}

		return Object.keys(partialData).length > 0 ? partialData : null;
	}
};
